import { useEffect, useState, useContext, useRef } from "react";
import { Grid, InputAdornment, IconButton, Paper, TextField, Button, TablePagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import styles from "./styles.module.css";
import { ROUTES, KEYBOARD_KEYS, EVENTS, ROUTE_PREFIXES } from "const";
import { eventsEmitter } from "utils/events";
import CustomerContext from "context/CustomerContext";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import DataTable from "components/utils/DataTable";
import Info from "components/utils/Alert/Info";
import AddCustomer from "components/popups/AddCustomer";
import { ReactComponent as CustomerIcon } from "images/svg/customer.svg";
import SearchIcon from "images/svg/search.svg";
import ShopContext from "context/ShopContext";
import validityExpired from "components/utils/validityExpired";
import RestrictionInfo from "components/utils/Alert/RestrictionInfo";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";

function Customers() {
  const { selectedShop } = useContext(ShopContext);
  const { customers: contextCustomers, loading, addCustomer: contextAddCustomer } = useContext(CustomerContext);

  const [customers, setCustomers] = useState([]);
  const [addCustomer, setAddCustomer] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const navigate = useNavigate();

  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === KEYBOARD_KEYS.F1) {
        e.preventDefault();
        e.stopPropagation();
        searchInputRef.current?.focus();
      }
    };
    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, []);

  const headerData = [
    {
      label: "Name",
      id: "name",
      type: "text",
    },
    {
      label: "Mobile",
      id: "mobile",
      type: "text",
    },
    {
      label: "Place",
      id: "location",
      type: "text",
    },
    {
      label: "Group",
      id: "group",
      type: "callback",
      viewRender: (row) => row.group?.name ?? "-",
    },
    {
      label: "Loyalty Card",
      id: "loyalty_card_no",
      type: "callback",
      viewRender: (row) => (row.loyalty_card ? `XXXX XXXX XXXX ${row.loyalty_card.slice(-4)}` : "-"),
    },
    {
      label: "Loyalty Point",
      id: "points",
      type: "floatAmount",
    },
    {
      label: "Credit Limit",
      id: "credit_limit",
      type: "callback",
      viewRender: (row) => NumberFormatHelper.customerAmountFormat(row.credit_limit),
    },
    {
      label: "Balance",
      id: "balance",
      type: "callback",
      viewRender: (row) => NumberFormatHelper.customerAmountFormat(row.balance),
    },
  ];

  const handleCustomerDetails = (cust) => {
    const id = cust.id || `${ROUTE_PREFIXES.LOCAL_CUSTOMER}${cust.local_id}`;
    navigate(ROUTES.CUSTOMER_DETAILS.replace(":customerId", id));
  };

  const handleAddCustomerDialog = () => {
    setAddCustomer(true);
  };

  const handleAddCusotmer = async (customer) => {
    try {
      await contextAddCustomer(customer);
      setAddCustomer(false);
      toast.success("Customer created successfully");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSearch = (value) => {
    setFilterText(value);
  };

  useEffect(() => {
    if (!contextCustomers) return;

    const filtered = contextCustomers.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
        (c.mobile || "").toLowerCase().includes(filterText.toLowerCase()) ||
        (c.location || "").toLowerCase().includes(filterText.toLowerCase()),
    );

    setCustomers(filtered);
    setPage(0);
  }, [contextCustomers, filterText]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCustomers = customers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Grid style={{ padding: "10px", height: "100%" }}>
      <Grid container className={styles.container} rowGap={1.25} alignItems="flex-start" alignContent="flex-start">
        <Grid item xs={12} className={styles.headerContainer}>
          <Grid className={styles.header}>
            <h2>Customers</h2>
            <Grid>
              <h5>Manage and Track Customer Details</h5>
            </Grid>
          </Grid>

          <Grid>
            <Grid item xs={12} className={styles.contentWrapper}>
              <TextField
                inputRef={searchInputRef}
                placeholder="Search"
                size="small"
                className={styles.searchField}
                value={filterText}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" style={{ padding: "0" }}>
                        <img src={SearchIcon} alt="Search" className={styles.icon} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className={styles.bar}> </div>
              <Button
                onClick={handleAddCustomerDialog}
                className={styles.addCustBtn}
                disabled={selectedShop && validityExpired(selectedShop.customer_support_validity)}
              >
                <CustomerIcon className={styles.icon} />
                Add New Customer
              </Button>
            </Grid>
          </Grid>
        </Grid>
        {selectedShop && validityExpired(selectedShop.customer_support_validity) ? (
          <Grid item xs={12} style={{ padding: "0 10px" }}>
            <RestrictionInfo
              title={"Customer Support Feature not available "}
              content={"To get Customer support options, contact support team."}
            />
          </Grid>
        ) : (
          <Grid item xs={12} className={styles.tableContainer}>
            {loading ? (
              <SkeletonLoading isOpen={true} count={40} height={40} xs={12} lg={1.5} />
            ) : customers && customers.length ? (
              <Paper
                elevation={0}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div className={styles.tableContent}>
                  <DataTable
                    columns={headerData}
                    rows={paginatedCustomers}
                    rowClickListener={(row) => handleCustomerDetails(row)}
                  />
                </div>
                <div className={styles.pagination}>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={customers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </div>
              </Paper>
            ) : (
              <Info
                title="No customers to list"
                content="You have no customers to list with current filter configuration. Please clear the filters or create a customer"
              />
            )}
          </Grid>
        )}
      </Grid>
      {addCustomer && <AddCustomer closeDialog={() => setAddCustomer(false)} addCustomerHandler={handleAddCusotmer} />}
    </Grid>
  );
}

export default Customers;
